export const BATTLE_ROOM_SNAPSHOT_VERSION = 2;

export const BATTLE_ROOM_LAYER_IDS = Object.freeze({
  MAP: 'map',
  TOKEN: 'token',
  GAMEMASTER: 'gamemaster'
});

export const BATTLE_ROOM_RUNTIME_OVERLAY_KEYS = Object.freeze([
  // Preserve complete embedded mechanics and per-instance economy on restore.
  // AGENTS.md snapshot fidelity; date_of_change: 2026-09-05.
  'contentVersion', 'traits', 'actions', 'bonusActions', 'reactions',
  'resistanceBlock', 'conditionImmunities', 'savingThrows', 'equipmentState', 'combatFlags',
  'hp',
  'maxHp',
  'hpObj',
  'tempHp',
  'ac',
  'initiative',
  'speed',
  'proficiencyBonus',
  'abilities',
  'stats',
  'statusEffects',
  'statusEffectsMeta',
  'conditions',
  'conditionsMeta',
  'movementUsed',
  'movementRemaining',
  'dashUsed',
  'gmNotes',
  'notes',
  'runtimeOverrides',
  'sheetOverrides'
]);

export const BATTLE_ROOM_VISUAL_STATE_KEYS = Object.freeze([
  'id',
  'name',
  'layer',
  'x',
  'y',
  'width',
  'height',
  'grid',
  'gridX',
  'gridY',
  'sizeCells',
  'rotation',
  'scaleX',
  'scaleY',
  'src',
  'size',
  'draggable',
  'hidden',
  'controlledBy',
  'controlType'
]);

export const pickDefinedEntries = (source, keys) => (
  (Array.isArray(keys) ? keys : []).reduce((accumulator, key) => {
    if (!source || typeof source !== 'object') {
      return accumulator;
    }

    if (source[key] !== undefined) {
      accumulator[key] = source[key];
    }

    return accumulator;
  }, {})
);

export const normalizeSnapshotEntityType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'monster') return 'monster';
  return 'character';
};

export const normalizeSnapshotLayerId = (value) => {
  const normalized = String(value || BATTLE_ROOM_LAYER_IDS.TOKEN).trim().toLowerCase();
  if (normalized === 'gm') {
    return BATTLE_ROOM_LAYER_IDS.GAMEMASTER;
  }
  if (normalized === BATTLE_ROOM_LAYER_IDS.MAP) {
    return BATTLE_ROOM_LAYER_IDS.MAP;
  }
  if (normalized === BATTLE_ROOM_LAYER_IDS.GAMEMASTER) {
    return BATTLE_ROOM_LAYER_IDS.GAMEMASTER;
  }
  return BATTLE_ROOM_LAYER_IDS.TOKEN;
};

export const isCharacterSnapshot = (value) => (
  normalizeSnapshotEntityType(value?.type || value?.entityRef?.entityType) === 'character'
);

export const isMonsterSnapshot = (value) => (
  normalizeSnapshotEntityType(value?.type || value?.entityRef?.entityType) === 'monster'
);

export const extractSnapshotEntityReference = (token) => {
  const entityType = normalizeSnapshotEntityType(
    token?.type
    || token?.entityRef?.entityType
    || token?.entity?.type
    || token?.characterData?.type
    || token?.monsterData?.type
  );

  return {
    entityId: token?.entityRef?.entityId
      || token?.baseTemplate
      || token?.characterDataRef
      || token?.monsterDataRef
      || token?.entity?.id
      || token?.originalUnifiedEntity?.id
      || token?.characterData?.id
      || token?.monsterData?.id
      || token?.id
      || null,
    entityType,
    sourceKind: token?.entityRef?.sourceKind
      || (token?.isCustom ? 'custom' : 'template'),
    canonicalId: token?.entityRef?.canonicalId
      || token?.originalUnifiedEntity?.id
      || token?.baseTemplate
      || null
  };
};

export const buildSnapshotMetadata = (token) => ({
  snapshotVersion: BATTLE_ROOM_SNAPSHOT_VERSION,
  entityRef: extractSnapshotEntityReference(token)
});

const BattleRoomSnapshotContract = {
  BATTLE_ROOM_SNAPSHOT_VERSION,
  BATTLE_ROOM_LAYER_IDS,
  BATTLE_ROOM_RUNTIME_OVERLAY_KEYS,
  BATTLE_ROOM_VISUAL_STATE_KEYS,
  normalizeSnapshotEntityType,
  normalizeSnapshotLayerId,
  isCharacterSnapshot,
  isMonsterSnapshot,
  pickDefinedEntries,
  extractSnapshotEntityReference,
  buildSnapshotMetadata
};

export default BattleRoomSnapshotContract;
